<template>
  <ValidationObserver ref="form" v-slot="{ invalid }">
    <div class="absolute top-0 left-0 right-0 bottom-0 w-full overflow-hidden flex items-center text-center justify-center z-50" v-show="loading">
      <spinner line-fg-color="#922C49" size="55" :line-size="5"></spinner>
    </div>
    <form id="form" v-if="!loading" @submit.prevent="submitForm" class="flex flex-col h-full">
      <page-header :buttons="pageActions.buttons" :additional="pageActions.additional" @method="methodHandler">
        <template slot="title">{{ type }} Application Form</template>
      </page-header>
      <tabs :tabs="tabs" :active="startTab" @setTab="setActiveTab" ref="tabs"></tabs>
      <div class="bg-red-200 py-2 text-sm text-red-700 text-center w-full -mt-px" role="alert" v-show="invalid && submitted">Please complete all required fields</div>
      <div class="bg-red-200 py-2 text-sm text-red-700 text-center w-full -mt-px" v-if="errorMessage">{{ errorMessage }}</div>
      <div class="flex md:flex-row sm:flex-col flex-grow">
        <div class="md:w-3/4 sm:w-full px-6 bg-gray-200 border-r border-gray-400 pb-12" v-show="activeTab === 'details'">
          <ValidationObserver vid="details" :slim="true">
            <div class="mt-4 grid grid-cols-1 row-gap-5 col-gap-4 sm:grid-cols-12">
              <input-text class="sm:col-span-10"
                label="Application Name"
                name="name"
                placeholder="Application Name"
                v-model="item.name"
                rules="required"
                :required="true"/>
              <input-text class="sm:col-span-2"
                label="Prefix"
                name="prefix"
                placeholder="Prefix"
                v-model="item.prefix"
                rules="required"
                :required="true"/>
              <input-text class="sm:col-span-3"
                label="Limit <small>(Per Member)</small>"
                name="limit"
                dollar="true"
                v-model="item.limit"
                :required="false"/>
              <input-text class="sm:col-span-3"
                label="Allocated Budget"
                name="budget"
                dollar="true"
                v-model="item.budget"
                rules="required"
                :required="true"/>
              <input-date-time class="xl:col-span-3"
                label="Open Date"
                placeholder="Open Date"
                name="start"
                v-model="item.start"
                :initialValue="initialValue.start"
                type="date"
                format="dd/MM/yyyy"
                :auto="true"
                rules="required"
                :required="true"/>
              <input-date-time class="xl:col-span-3"
                label="Close Date"
                placeholder="Close Date"
                name="end"
                v-model="item.end"
                :initialValue="initialValue.end"
                type="date"
                format="dd/MM/yyyy"
                :auto="true"
                rules="required"
                :required="true"/>
              <input-text class="sm:col-span-3"
                label="Account Code"
                name="account_code"
                placeholder="Account Code"
                v-model="item.account_code"
                :required="false"/>
              <input-text class="sm:col-span-3"
                label="Remaining Threshold"
                name="remaining_threshold"
                dollar="true"
                v-model="item.remaining_threshold"
                tooltip="The minimum remaining amount required to be able to start a new application"
              />
              <input-text class="sm:col-span-3"
                label="Funding Account BSB"
                name="funding_account_bsb"
                placeholder="BSB"
                v-model="item.funding_account_bsb"
                :mask="'######'"
              />
              <input-text class="sm:col-span-3"
                label="Funding Account Number"
                name="funding_account_number"
                placeholder="Account Number"
                v-model="item.funding_account_number"
                :mask="'##########'"
              />
              <input-select class="sm:col-span-6"
                label="Xero Account"
                placeholder="Xero Account"
                name="xero_tenant"
                v-model="item.xero_tenant"
                return="value"
                :disabled="tenantsLoading"
                :options="item && item.tenants ? item.tenants.map((tenant) => ({ label: tenant.tenantName, value: tenant.tenantId })) : tenants"
              />
              <input-text class="sm:col-span-3"
                label="Default Account BSB"
                name="default_account_bsb"
                placeholder="BSB"
                v-model="item.default_account_bsb"
                :mask="'######'"
              />
              <input-text class="sm:col-span-3"
                label="Default Account Number"
                name="default_account_number"
                placeholder="Account Number"
                v-model="item.default_account_number"
                :mask="'##########'"
              />
            </div>
            <div class="mt-6">
              <h2 class="w-full py-2 border-b-2 border-charcoal-900 text-left font-medium text-charcoal-900 mb-3">Documents</h2>
              <span v-for="(file, index) in item.documents" :key="file.path || index" class="text-base flex flex-row items-center my-2">
                <a :href="S3URL + '/' + file.path" target="_blank" class="text-gray-800 hover:text-default-500 truncate">{{ file.filename }}</a>
                <button type="button" @click="removeDocument(index)">
                  <svg aria-hidden="true" focusable="false" data-prefix="far" data-icon="times" class="svg-inline--fa fa-times w-6 h-6 text-red-700 ml-2" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
                    <path fill="currentColor" d="M207.6 256l107.72-107.72c6.23-6.23 6.23-16.34 0-22.58l-25.03-25.03c-6.23-6.23-16.34-6.23-22.58 0L160 208.4 52.28 100.68c-6.23-6.23-16.34-6.23-22.58 0L4.68 125.7c-6.23 6.23-6.23 16.34 0 22.58L112.4 256 4.68 363.72c-6.23 6.23-6.23 16.34 0 22.58l25.03 25.03c6.23 6.23 16.34 6.23 22.58 0L160 303.6l107.72 107.72c6.23 6.23 16.34 6.23 22.58 0l25.03-25.03c6.23-6.23 6.23-16.34 0-22.58L207.6 256z"></path>
                  </svg>
                </button>
              </span>
              <file-upload name="documents" :folder="'files'" :multiple="true" @uploaded="documentsUploaded"></file-upload>
            </div>
          </ValidationObserver>
        </div>
        <div class="md:w-3/4 sm:w-full px-4 bg-gray-200 border-r border-gray-400 pb-12" v-show="activeTab === 'sms'">
          <ValidationObserver vid="details" :slim="true">
            <div class="mt-4 grid grid-cols-1 row-gap-5 col-gap-4 sm:grid-cols-12">
              <input-textarea class="sm:col-span-12"
                label="Instant Approval - SMS Notification Message"
                name="sms_instant_approval"
                ref="sms_instant_approval_input"
                v-model="item.sms_instant_approval"
                :required="false"/>
              <sms-insert-codes input="sms_instant_approval_input" output="sms_instant_approval" @insert="insertCode"/>
              <input-textarea class="sm:col-span-12"
                label="Pending Approval - SMS Notification Message"
                name="sms_pending_approval"
                ref="sms_pending_approval_input"
                v-model="item.sms_pending_approval"
                :required="false"/>
              <sms-insert-codes input="sms_pending_approval_input" output="sms_pending_approval" @insert="insertCode"/>
              <input-textarea class="sm:col-span-12"
                label="Changed to Approved - SMS Notification Message"
                name="sms_approved"
                ref="sms_approved_input"
                v-model="item.sms_approved"
                :required="false"/>
              <sms-insert-codes input="sms_approved_input" output="sms_approved" @insert="insertCode"/>
              <input-textarea class="sm:col-span-12"
                label="Changed to Unsuccessful - SMS Notification Message"
                name="sms_unsuccessful"
                ref="sms_unsuccessful_input"
                v-model="item.sms_unsuccessful"
                :required="false"/>
              <sms-insert-codes input="sms_unsuccessful_input" output="sms_unsuccessful" @insert="insertCode"/>
              <input-textarea class="sm:col-span-12"
                label="Changed to Completed - SMS Notification Message"
                name="sms_closed"
                ref="sms_closed_input"
                v-model="item.sms_closed"
                :required="false"/>
              <sms-insert-codes input="sms_closed_input" output="sms_closed" @insert="insertCode"/>
              <input-textarea class="sm:col-span-12"
                label="Incomplete Reminder - SMS Notification Message"
                name="sms_incomplete_reminder"
                ref="sms_incomplete_reminder_input"
                v-model="item.sms_incomplete_reminder"
                :required="false"/>
              <sms-insert-codes input="sms_incomplete_reminder_input" output="sms_incomplete_reminder" @insert="insertCode"/>
            </div>
          </ValidationObserver>
        </div>
        <div class="md:w-3/4 sm:w-full px-4 bg-gray-200 border-r border-gray-400 pb-12" v-show="activeTab === 'form'">
          <form-builder v-model="item.form" :data="item.form" :active="activeTab === 'form'"></form-builder>
        </div>
        <div class="md:w-3/4 sm:w-full px-4 bg-gray-200 border-r border-gray-400 pb-12" v-show="activeTab === 'suppliers'">
          <h2 class="px-1 pt-4 border-b-2 border-black pb-1 font-semibold">Supplier Types</h2>
          <p class="bg-blue-100 border-t border-b border-blue-500 text-blue-700 px-4 py-3 text-xs mt-4">
            Select the Supplier Types available to the user on this Application Form. You can leave this blank and select individual Suppliers below.
          </p>
          <div v-if="!supplierTypesLoading">
            <input type="text" v-model="searchSupplierTypes" value="" placeholder="Filter Supplier Types by Name..." class="form-input w-full my-3 text-sm"/>
            <table class="min-w-full text-xs mb-6">
              <thead>
              <tr>
                <th class="table-head-cell w-12">
                  <input type="checkbox" value="all" name="selectAll" v-model="selectAllSupplierTypes"></th>
                <th class="table-head-cell pl-0">Name</th>
              </tr>
              </thead>
              <tbody v-if="supplierTypes">
              <tr v-for="(item, index) in supplierTypes" v-bind:key="index">
                <td class="table-body-cell w-12 py-2" :class="[index % 2 !== 0 ? 'even' : 'odd' ]">
                  <input type="checkbox" :value="item.id" v-model="selectedSupplierTypes"></td>
                <td class="table-body-cell pl-0" :class="[ index % 2 !== 0 ? 'even' : 'odd' ]">{{ item.name }}</td>
              </tr>
              </tbody>
            </table>
          </div>
          <h2 class="px-1 border-b-2 border-black pb-1 font-semibold">Suppliers</h2>
          <p class="bg-blue-100 border-t border-b border-blue-500 text-blue-700 px-4 py-3 text-xs mt-4">
            You only need to select individual Suppliers if you haven't selected any Types above or you want to limit to specific Suppliers in the selected Types.
          </p>
          <div v-if="!suppliersLoading">
            <input type="text" v-model="searchSuppliers" value="" placeholder="Filter Suppliers by Name..." class="form-input w-full my-3 text-sm"/>
            <table class="min-w-full text-xs mb-6">
              <thead>
              <tr>
                <th class="table-head-cell w-12">
                  <input type="checkbox" value="all" name="selectAll" v-model="selectAllSuppliers"></th>
                <th class="table-head-cell pl-0">Name</th>
                <th class="table-head-cell pl-0">Type</th>
              </tr>
              </thead>
              <tbody v-if="suppliers">
              <tr v-for="(item, index) in suppliers" v-bind:key="index">
                <td class="table-body-cell w-12 py-2" :class="[index % 2 !== 0 ? 'even' : 'odd' ]">
                  <input type="checkbox" :value="item.id" v-model="selectedSuppliers"></td>
                <td class="table-body-cell pl-0" :class="[ index % 2 !== 0 ? 'even' : 'odd' ]">{{ item.name }}</td>
                <td class="table-body-cell pl-0" :class="[ index % 2 !== 0 ? 'even' : 'odd' ]">{{ commaSeparatedTypes(item.types) }}</td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div class="md:w-3/4 sm:w-full px-4 bg-gray-200 border-r border-gray-400 pb-12" v-show="activeTab === 'members'">
          <h2 class="px-1 pt-4 border-b-2 border-black pb-1 font-semibold">Members
            <small class="font-normal">({{ selectedMembers.length }} Selected)</small></h2>
          <p class="bg-blue-100 border-t border-b border-blue-500 text-blue-700 px-4 py-3 text-xs mt-4">
            Select the Members who will have access to this Application Form. If none are selected it will be available to All Members.
          </p>
          <div v-if="!membersLoading">
            <div class="flex flex-row items-center">
              <input type="text" v-model="searchMembers" value="" placeholder="Filter Members by Name..." class="form-input w-full my-3 text-sm flex-grow mr-3"/>
              <input-select class="w-1/5 my-3 mr-3"
                label=""
                name="member_type"
                placeholder="Member Type"
                :options="memberTypes"
                v-model="memberTypeSearch"
                :required="false"/>
              <button type="button"
                class="flex-shrink-0 whitespace-no-wrap px-4 text-sm bg-default-500 hover:bg-default-600 text-white border border-gray-400 cursor-pointer"
                style="height: 39px;"
                @click="showCopyMembersModal = true">
                Copy Members
              </button>
            </div>
            <table class="min-w-full text-xs">
              <thead>
              <tr>
                <th class="table-head-cell w-12">
                  <input type="checkbox" value="all" name="selectAll" v-model="selectAllMembers"></th>
                <th class="table-head-cell pl-0">Member ID</th>
                <th class="table-head-cell pl-0">Name</th>
                <th class="table-head-cell pl-0">Type</th>
                <th class="table-head-cell pl-0">DOB</th>
                <th class="table-head-cell pl-0 text-center">Age</th>
              </tr>
              </thead>
              <tbody v-if="members">
              <tr v-for="(item, index) in members" v-bind:key="index">
                <td class="table-body-cell w-12 py-2" :class="[index % 2 !== 0 ? 'even' : 'odd' ]">
                  <input type="checkbox" :value="item.id" v-model="selectedMembers"></td>
                <td class="table-body-cell pl-0" :class="[ index % 2 !== 0 ? 'even' : 'odd' ]">{{ item.member_id_display }}</td>
                <td class="table-body-cell pl-0" :class="[ index % 2 !== 0 ? 'even' : 'odd' ]">
                  {{ item.first }} {{ item.last }}<span v-if="!item.active" class="text-red-500"> (Inactive)</span>
                </td>
                <td class="table-body-cell pl-0" :class="[ index % 2 !== 0 ? 'even' : 'odd' ]">{{ item.type ? item.type.name : '' }}</td>
                <td class="table-body-cell pl-0" :class="[ index % 2 !== 0 ? 'even' : 'odd' ]">{{ item.date_of_birth }}</td>
                <td class="table-body-cell pl-0 text-center" :class="[ index % 2 !== 0 ? 'even' : 'odd' ]">{{ item.age }}</td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div class="md:w-3/4 sm:w-full pt-2 px-6 bg-gray-200 border-r border-gray-400 pb-12" v-show="activeTab === 'faq'">
          <ValidationObserver vid="faq" :slim="true">
            <div class="grid grid-cols-1 row-gap-4 col-gap-4 sm:grid-cols-12">
              <h2 class="w-full sm:col-span-12 py-2 border-b-2 border-charcoal-900 text-left font-medium text-charcoal-900">New FAQ</h2>
              <input-text class="sm:col-span-12"
                label="Question"
                name="question"
                v-model="faqNewQuestion"
                :required="false"/>
              <input-textarea class="sm:col-span-12"
                label="Answer"
                name="answer"
                v-model="faqNewAnswer"
                :required="false"/>
              <div class="col-span-12">
                <button type="button" @click="addFaq" class="inline-block px-4 py-1 text-sm bg-default-500 text-white border border-gray-400 cursor-pointer">Add FAQ</button>
              </div>
            </div>
            <div class="grid grid-cols-1 row-gap-5 col-gap-4 sm:grid-cols-12 mt-4" v-if="item.faqs && item.faqs.length > 0">
              <h2 class="w-full sm:col-span-12 py-2 border-b-2 border-charcoal-900 text-left font-medium text-charcoal-900">FAQs</h2>
              <div class="col-span-12" v-for="(faq,index) in item.faqs" :key="index">
                <div v-if="currentFaqForEdit.index !== index" class="relative">
                  <div class="flex flex-col border-b border-gray-400 pb-2 mb-2">
                    <span class="col-span-12 text-md text-bold max-w-192">Q: {{ faq.question }}</span>
                    <span class="col-span-12 mt-1 text-sm">A: {{ faq.answer }}</span>
                  </div>
                  <div v-if="currentFaqForEdit.index === null" class="col-span-12 mt-0 flex justify-end absolute right-0 top-0">
                    <font-awesome-icon class="text-base text-grey-300 mr-3 cursor-pointer"
                      @click="editFaq(index)"
                      :icon="['fas', 'edit']"/>
                    <font-awesome-icon class="text-base text-grey-300 mr-3 cursor-pointer"
                      @click="item.faqs.splice(index, 1)"
                      :icon="['fas', 'trash-alt']"/>
                  </div>
                </div>
                <!-- Edit FAQ -->
                <div class="grid grid-cols-1 row-gap-4 col-gap-4 sm:grid-cols-12" v-if="currentFaqForEdit.index === index">
                  <input-text class="sm:col-span-12"
                    label="Question"
                    name="question"
                    v-model="currentFaqForEdit.question"
                    :required="false"/>
                  <input-textarea class="sm:col-span-12"
                    label="Answer"
                    name="answer"
                    v-model="currentFaqForEdit.answer"
                    :required="false"/>
                  <div class="col-span-12 mt-2">
                    <button type="button" @click="saveFaqAfterEdit(index)" class="inline-block px-4 py-1 text-sm bg-default-500 text-white cursor-pointer">Save</button>
                    <button type="button" @click="currentFaqForEdit.index = null" class="inline-block px-4 py-1 ml-4 text-sm bg-gray-300 border border-gray-400 cursor-pointer">Cancel</button>
                  </div>
                </div>
              </div>
            </div>
          </ValidationObserver>
        </div>
        <div class="md:w-3/4 sm:w-full px-6 pt-2 bg-gray-200 border-r border-gray-400 pb-12" v-show="activeTab === 'completion'">
          <ValidationObserver vid="faq" :slim="true">
            <div class="grid grid-cols-1 row-gap-5 col-gap-4 sm:grid-cols-12">
              <h2 class="w-full sm:col-span-12 py-2 border-b-2 border-charcoal-900 text-left font-medium text-charcoal-900">What Happens Next</h2>
              <input-text class="sm:col-span-12"
                label="Step 1"
                name="step1"
                v-model="item.steps.step1"
                :required="false"/>
              <input-text class="sm:col-span-12"
                label="Step 2"
                name="step2"
                v-model="item.steps.step2"
                :required="false"/>
              <input-text class="sm:col-span-12"
                label="Step 3"
                name="step3"
                v-model="item.steps.step3"
                :required="false"/>
              <input-text class="sm:col-span-12"
                label="Step 4"
                name="step4"
                v-model="item.steps.step4"
                :required="false"/>
              <input-text class="sm:col-span-12"
                label="Step 5"
                name="step5"
                v-model="item.steps.step5"
                :required="false"/>
            </div>
          </ValidationObserver>
        </div>
        <div class="md:w-1/4 sm:w-full px-4 sm:px-6 lg:px-6">
          <ValidationObserver vid="details" :slim="true">
            <div class="mt-4 grid grid-cols-1 row-gap-5 col-gap-4 sm:grid-cols-12">
              <input-toggle class="sm:col-span-6"
                label="Active"
                name="active"
                v-model="item.active"
                :required="false"/>
              <input-toggle class="sm:col-span-6"
                label="Instant Approval"
                name="instant_approval"
                v-model="item.instant_approval"
                :required="false"/>
              <input-toggle class="sm:col-span-6"
                label="Suppliers Visible"
                name="suppliers_visible"
                v-model="item.suppliers_visible"
                :required="false"/>
              <input-toggle class="sm:col-span-6"
                label="Amount Visible"
                name="amount_visible"
                v-model="item.amount_visible"
                :required="false"/>
              <input-toggle class="sm:col-span-6"
                label="Single Application"
                name="single_application"
                v-model="item.single_application"
                :required="false"/>
              <input-toggle class="sm:col-span-6"
                label="Hidden From App"
                name="hidden"
                v-model="item.hidden"
                :required="false"/>
              <input-toggle class="sm:col-span-6"
                label="Analytics"
                name="analytics"
                v-model="item.analytics"
                tooltip="If enabled, any newly created submissions will be included in the analytics/dashboard."
                :required="false"/>
              <input-toggle class="sm:col-span-6"
                label="QR Code"
                name="qr_code"
                v-model="item.qr_code"
                tooltip="If enabled, communications can include a QR link merge tag for this form."
                :required="false"/>
              <input-toggle class="sm:col-span-12"
                label="Hide Form Responses"
                name="hide_form_responses"
                v-model="item.hide_form_responses"
                tooltip="If enabled, submitted form answers are hidden when viewing a submission."
                :required="false"/>
              <input-color class="sm:col-span-12"
                label="Background Colour"
                :value="item.background_colour"
                name="background_colour"
                v-model="item.background_colour"/>
              <input-text class="sm:col-span-6"
                type="number"
                label="Birth Year From"
                name="birth_year_from"
                placeholder="YYYY"
                v-model="item.birth_year_from"
                :required="false"/>
              <input-text class="sm:col-span-6"
                type="number"
                label="Birth Year To"
                name="birth_year_to"
                placeholder="YYYY"
                v-model="item.birth_year_to"
                :required="false"/>
              <div class="sm:col-span-12 flex flex-col">
                <label class="block text-sm font-medium leading-5 text-gray-800 relative pb-1">Icon</label>
                <div class="relative flex flex-row justify-center items-center mb-2" :style="'background: '+item.background_colour+';'" v-if="item.icon_url">
                  <font-awesome-icon class="-mt-5 absolute right-0 top-0 text-red-700 text-2xl cursor-pointer h-4" :icon="['fas', 'times']" @click="clearIcon()"/>
                  <img :src="S3URL+'/'+item.icon_url" class="p-4 mb-2" alt="Icon"/>
                </div>
                <file-upload name="icon" id="icon" v-model="item.icon_url" :folder="'icons'" @uploaded="iconUploaded"></file-upload>
              </div>
              <input-toggle class="sm:col-span-12"
                label="Featured"
                name="featured"
                v-model="item.featured"
                tooltip="If enabled, members see this form on the dashboard above Start New Application."
                :required="false"/>
            </div>
          </ValidationObserver>
        </div>
      </div>
    </form>
    <copy-members-modal
      v-if="showCopyMembersModal"
      :exclude-id="id"
      @close="showCopyMembersModal = false"
      @copied="applyCopiedMembers"/>
  </ValidationObserver>
</template>

<script>
import axios from 'axios'
import FormSetup from '../../mixins/Tabs'
import FormButtons from '../../mixins/FormButtons'
import InputTextarea from '../../components/forms/InputTextarea.vue'
import FileUpload from '../../components/forms/FileUpload.vue'
import FormBuilder from './FormBuilder'
import SmsInsertCodes from './SmsInsertCodes'
import CopyMembersModal from './modals/CopyMembersModal'

export default {
  components: { FormBuilder, InputTextarea, FileUpload, SmsInsertCodes, CopyMembersModal },
  name: 'ApplicationForm',
  props: {
    id: { required: false },
    type: { required: true },
    return: { required: false, default: '/application-forms' },
    page: { required: false, default: 'application-forms' }
  },
  mixins: [
    FormSetup,
    FormButtons
  ],
  data: function () {
    return {
      resource: 'application-forms',
      faqNewQuestion: '',
      faqNewAnswer: '',
      currentFaqForEdit: {
        index: null,
        question: '',
        answer: ''
      },
      pageActions: {
        buttons: [
          {
            label: 'Save',
            icon: 'save',
            iconClass: 'text-green-600',
            type: 'method',
            method: 'save'
          },
          {
            label: 'View',
            icon: 'eye',
            iconClass: 'text-blue-600',
            type: 'method',
            method: 'view',
            display: this.type === 'Edit'
          },
          {
            label: 'Cancel',
            icon: 'times',
            iconClass: 'text-red-600',
            type: 'method',
            method: 'cancel'
          }
        ],
        additional: []
      },
      tabs: [
        {
          name: 'Details',
          slug: 'details'
        },
        {
          name: 'SMS',
          slug: 'sms'
        },
        {
          name: 'Form',
          slug: 'form'
        },
        {
          name: 'Suppliers',
          slug: 'suppliers'
        },
        {
          name: 'Members',
          slug: 'members'
        },
        {
          name: 'FAQ\'s',
          slug: 'faq'
        },
        {
          name: 'Completion Screen',
          slug: 'completion'
        }
      ],
      startTab: null,
      loading: true,
      submitted: false,
      errorMessage: '',
      showPostalAddress: 'hide',
      item: {
        name: '',
        limit: '',
        budget: '',
        icon_url: '',
        icon: '',
        sms_notification: '',
        active: true,
        instant_approval: true,
        suppliers_visible: true,
        amount_visible: true,
        single_application: true,
        qr_code: false,
        featured: false,
        hide_form_responses: false,
        faqs: [],
        documents: [],
        steps: {
          step1: '',
          step2: '',
          step3: '',
          step4: '',
          step5: ''
        }
      },
      S3URL: process.env.VUE_APP_S3_URL,
      searchSuppliers: '',
      originalSuppliers: [],
      selectedSuppliers: [],
      suppliersLoading: true,
      searchSupplierTypes: '',
      originalSupplierTypes: [],
      selectedSupplierTypes: [],
      supplierTypesLoading: true,
      searchMembers: '',
      originalMembers: [],
      selectedMembers: [],
      previouslySelectedMembers: [],
      membersLoading: true,
      memberTypes: [],
      memberTypeSearch: null,
      showCopyMembersModal: false,
      tenants: [],
      tenantsLoading: false,
    }
  },
  methods: {
    iconUploaded: function (payload) {
      this.item.icon_url = payload.data[0].path
    },
    documentsUploaded: function (payload) {
      if (!this.item.documents) {
        this.$set(this.item, 'documents', [])
      }

      payload.data.forEach(file => {
        this.item.documents.push(file)
      })
    },
    removeDocument: function (index) {
      this.item.documents.splice(index, 1)
    },
    insertCode: function (payload) {
      this.$nextTick(() => {
        let textarea = this.$refs[payload.input]
        let cursorPosition = textarea.$refs[payload.output].selectionStart

        const content = this.item[payload.output]

        if (content && content !== '') {
          const textBefore = content.substring(0, cursorPosition)
          const textAfter = content.substring(cursorPosition, content.length)

          this.item[payload.output] = textBefore + payload.code + ' ' + textAfter
        } else {
          this.item[payload.output] = payload.code + ' '
        }

        textarea.$refs[payload.output].focus()
        textarea.$refs[payload.output].blur()
        textarea.$refs[payload.output].focus()
      })
    },
    clearIcon: function () {
      this.item.icon_url = null
    },
    addFaq: function () {
      this.errorMessage = ''

      if (this.faqNewQuestion === '' || this.faqNewAnswer === '') {
        this.errorMessage = 'Please add both a question and answer'
      } else {
        if (this.item.faqs) {
          this.item.faqs.unshift(
            {
              question: this.faqNewQuestion,
              answer: this.faqNewAnswer
            }
          )
        } else {
          this.item.faqs = []
          this.item.faqs.push(
            {
              question: this.faqNewQuestion,
              answer: this.faqNewAnswer
            }
          )
        }
        this.faqNewQuestion = ''
        this.faqNewAnswer = ''
      }
      setTimeout(() => this.errorMessage = '', 5000)
    },
    editFaq: function (index) {
      this.currentFaqForEdit = {
        index: index,
        question: this.item.faqs[index].question,
        answer: this.item.faqs[index].answer
      }
    },
    saveFaqAfterEdit: function (index) {
      this.errorMessage = ''

      if (this.currentFaqForEdit.question === '') {
        this.errorMessage = 'Please enter a question first'
      } else if (this.currentFaqForEdit.answer === '') {
        this.errorMessage = 'Please enter an answer for the question'
      } else {
        this.item.faqs[index].question = this.currentFaqForEdit.question
        this.item.faqs[index].answer = this.currentFaqForEdit.answer
        this.currentFaqForEdit.index = null
      }
      setTimeout(() => this.errorMessage = '', 5000)
    },
    getData: function () {
      this.$Progress.start()

      axios.get(process.env.VUE_APP_API_URL + '/application-forms/' + this.id, {
        params: {
          include: 'xero-tenants'
        }
      }).then(response => {
        if (response.data.form) {
          response.data.form.elements.forEach((element) => {
            if (element.type === 'textInput') {
              if (!element.mask) {
                element.mask = {
                  visible: false,
                  value: ''
                }
              }
            }
          })
        }

        this.item = response.data

        if (!this.item.documents) {
          this.item.documents = []
        }

        if (this.item.suppliers) {
          this.selectedSuppliers = this.item.suppliers.suppliers
          this.selectedSupplierTypes = this.item.suppliers.types
        }

        if (this.item.members) {
          this.selectedMembers = this.item.members
          this.previouslySelectedMembers = this.item.members.slice()
        }

        this.getMembers()

        this.$Progress.finish()
        this.loading = false
      })
        .catch(() => {
          this.getMembers()
          this.$Progress.finish()
          this.loading = false
        })
    },
    submitForm: function () {
      this.submitted = false

      this.$refs.form.validate().then(success => {
        let _method
        let _url
        if (!success) {
          this.submitted = true
          return
        }

        this.submitted = true

        this.$store.commit('TOGGLE_BACKDROP', { open: true })
        this.errorMessage = ''

        if (this.id) {
          _url = process.env.VUE_APP_API_URL + '/application-forms/' + this.id
          _method = 'PUT'
        } else if (this.item.id) {
          _url = process.env.VUE_APP_API_URL + '/application-forms/' + this.item.id
          _method = 'PUT'
        } else {
          _url = process.env.VUE_APP_API_URL + '/application-forms'
          _method = 'POST'
        }

        this.item.suppliers = {
          types: this.selectedSupplierTypes,
          suppliers: this.selectedSuppliers
        }

        this.item.members = this.selectedMembers

        axios({
          method: _method,
          url: _url,
          data: this.item
        }).then(response => {
          if (response.data.success) {
            this.$store.commit('TOGGLE_BACKDROP', { open: false })
            this.$store.dispatch('GET_APPLICATION_FORMS')

            if (this.saveClose) {
              if (this.return) {
                this.$router.push(this.return)
              } else {
                this.$router.push('/application-forms')
              }
            } else {
              this.$router.push('/application-forms/' + response.data.data.id + '/view')
              //this.item.id = response.data.data.id;
            }

            this.$notify({
              group: 'main',
              type: 'success',
              title: 'Success',
              text: response.data.message,
              duration: 5000
            })

          } else {
            if (response.data.message) {
              this.errorMessage = response.data.message
            }
            this.$store.commit('TOGGLE_BACKDROP', { open: false })
          }
        }).catch(error => {
          if (error.response.data.message) {
            this.errorMessage = error.response.data.message
          } else {
            this.errorMessage = 'An error occurred. Please contact the System Administrator.'
          }

          this.$store.commit('TOGGLE_BACKDROP', { open: false })
        })
      })
    },
    cancel: function () {
      this.$router.push(this.return)
    },
    methodHandler: function (method) {
      this[method]()
    },
    actionHandler: function (data) {
      this[data.method](data)
    },
    getSuppliers: function () {
      axios.get(process.env.VUE_APP_API_URL + '/suppliers').then(response => {
        this.originalSuppliers = response.data.map(function (supplier) {
          return {
            id: supplier.id,
            name: supplier.name,
            types: supplier.types,
            type_ids: supplier.types.map(type => type.id)
          }
        })

        this.suppliersLoading = false
      })
    },
    getTenants: function () {
      this.tenantsLoading = true

      axios.get(process.env.VUE_APP_API_URL + '/xero-tenants')
        .then(response => {
          this.tenants = response.data
            .map((tenant) => ({
              label: tenant.tenantName,
              value: tenant.id
            }))
        })
        .finally(() => {
          this.tenantsLoading = false
        })
    },
    getSupplierTypes: function () {
      axios.get(process.env.VUE_APP_API_URL + '/types', {
        params: {
          filters: { group: { name: 'group', value: 'supplier-types' } },
          sortColumn: 'name',
          sortDir: 'ASC'
        }
      }).then(response => {
        this.originalSupplierTypes = response.data.map(function (type) {
          return { id: type.id, name: type.name }
        })

        this.supplierTypesLoading = false
      })
    },
    getMembers: function () {
      const filters = {
        active: {
          value: 1
        }
      }

      // Resolve assigned members on the server so we don't put hundreds of IDs in the query string
      if (this.id) {
        filters.application_form_id = {
          value: this.id
        }
      }

      axios.get(process.env.VUE_APP_API_URL + '/members', {
        params: {
          sortColumn: 'first',
          sortDir: 'ASC',
          simple: true,
          filters
        }
      }).then(response => {
        this.originalMembers = response.data

        let result = []
        response.data.forEach(member => {
          if (member.type) {
            if (result.indexOf(member.type.name) === -1) {
              result.push(member.type.name)
            }
          }
        })

        this.memberTypes = result.map(type => {
          return { label: type, value: type }
        })
      }).finally(() => {
        this.membersLoading = false
      })
    },
    commaSeparatedTypes(types) {
      const fieldNames = types.map(object => {
        return object.name
      })
      return fieldNames.join(', ')
    },
    applyCopiedMembers(memberIds) {
      const existing = new Set(this.selectedMembers.map(id => String(id)))
      let added = 0

      memberIds.forEach(id => {
        if (!existing.has(String(id))) {
          this.selectedMembers.push(id)
          existing.add(String(id))
          added++
        }
      })

      this.showCopyMembersModal = false

      this.$notify({
        group: 'main',
        type: 'success',
        title: 'Members copied',
        text: added === 0
          ? 'Those members are already selected.'
          : `Added ${added} member${added === 1 ? '' : 's'}. Save the form to keep this selection.`,
        duration: 5000
      })
    }
  },
  beforeMount() {
    this.getSupplierTypes()
    this.getSuppliers()
    if (this.type === 'New') {
      this.getMembers()
      this.getTenants()
    }
  },
  mounted() {
    if (this.type === 'New') {
      this.loading = false
      this.item.active = true
      this.$Progress.finish()
      // Se defaults here
    } else if (this.type === 'Edit') {
      this.pageActions.additional.push({
        label: 'Delete',
        icon: 'trash-alt',
        type: 'method',
        method: 'deleteModal',
        iconClass: 'text-red-500',
        display: !!(this.$store.getters.permissions.indexOf('applicationForms.destroy') !== -1 || this.$store.getters.nimdarepus)
      })
      this.getData()
    }

    const params = new URLSearchParams(window.location.search)
    let windowLocationHash = window.location.hash.replace('#', '')

    if (windowLocationHash) {
      this.startTab = windowLocationHash
    }
  },
  computed: {
    initialValue() {
      let output = {}

      if (this.item && this.item.start) {
        output['start'] = this.item.start
      } else {
        output['start'] = null
      }

      if (this.item && this.item.end) {
        output['end'] = this.item.end
      } else {
        output['end'] = null
      }

      return output
    },
    supplierTypes() {
      if (this.searchSupplierTypes) {
        return this.originalSupplierTypes.filter(item => item.name.toUpperCase().includes(this.searchSupplierTypes.toUpperCase()))
      }
      return this.originalSupplierTypes
    },
    suppliers() {
      if (this.selectedSupplierTypes.length > 0) {
        if (this.searchSuppliers) {
          return this.originalSuppliers.filter(supplier => {
            let found = false

            this.selectedSupplierTypes.forEach(type => {
              if (supplier.type_ids.indexOf(type) !== -1) {
                found = true
              }
            })

            return found
          }).filter(item => item.name.toUpperCase().includes(this.searchSuppliers.toUpperCase()))
        } else {
          return this.originalSuppliers.filter(supplier => {
            let found = false

            this.selectedSupplierTypes.forEach(type => {
              if (supplier.type_ids.indexOf(type) !== -1) {
                found = true
              }
            })

            return found
          })
        }
      } else if (this.searchSuppliers) {
        return this.originalSuppliers.filter(item => item.name.toUpperCase().includes(this.searchSuppliers.toUpperCase()))
      }

      return this.originalSuppliers
    },
    eligibleMembers() {
      // Inactive members stay in the list only if they were already assigned to this form
      return this.originalMembers.filter(member => {
        if (!member || !member.id) {
          return false
        }

        if (member.active) {
          return true
        }

        return this.previouslySelectedMembers.some(id => String(id) === String(member.id))
      })
    },
    members() {
      const members = this.eligibleMembers

      if (this.selectedMembers.length > 0) {
        if (this.searchMembers) {
          return members.filter(item => {
            if (!item.first || !item.last) {
              return false
            }
            return item.first.toUpperCase().includes(this.searchMembers.toUpperCase()) || item.last.toUpperCase().includes(this.searchMembers.toUpperCase())
          }).filter(item => {
            if (this.memberTypeSearch) {
              return item.type.name === this.memberTypeSearch
            } else {
              return true
            }
          }).filter(item => {
            if (this.item.birth_year_from && this.item.birth_year_to) {
              return item.year_of_birth >= this.item.birth_year_from && item.year_of_birth <= this.item.birth_year_to
            } else if (this.item.birth_year_from) {
              return item.year_of_birth >= this.item.birth_year_from
            } else if (this.item.birth_year_from) {
              return item.year_of_birth <= this.item.birth_year_to
            } else {
              return true
            }
          })
        } else {
          if (this.memberTypeSearch) {
            return members.filter(item => {
              return item.type.name === this.memberTypeSearch
            }).filter(item => {
              if (this.item.birth_year_from && this.item.birth_year_to) {
                return item.year_of_birth >= this.item.birth_year_from && item.year_of_birth <= this.item.birth_year_to
              } else if (this.item.birth_year_from) {
                return item.year_of_birth >= this.item.birth_year_from
              } else if (this.item.birth_year_from) {
                return item.year_of_birth <= this.item.birth_year_to
              } else {
                return true
              }
            })
          } else {
            return members.filter(item => {
              if (this.item.birth_year_from && this.item.birth_year_to) {
                return item.year_of_birth >= this.item.birth_year_from && item.year_of_birth <= this.item.birth_year_to
              } else if (this.item.birth_year_from) {
                return item.year_of_birth >= this.item.birth_year_from
              } else if (this.item.birth_year_from) {
                return item.year_of_birth <= this.item.birth_year_to
              } else {
                return true
              }
            })
          }
        }
      } else if (this.searchMembers) {
        return members.filter(item => {
          return item.first.toUpperCase().includes(this.searchMembers.toUpperCase()) || item.last.toUpperCase().includes(this.searchMembers.toUpperCase())
        }).filter(item => {
          if (this.memberTypeSearch) {
            return item.type.name === this.memberTypeSearch
          }
        }).filter(item => {
          if (this.item.birth_year_from && this.item.birth_year_to) {
            return item.year_of_birth >= this.item.birth_year_from && item.year_of_birth <= this.item.birth_year_to
          } else if (this.item.birth_year_from) {
            return item.year_of_birth >= this.item.birth_year_from
          } else if (this.item.birth_year_from) {
            return item.year_of_birth <= this.item.birth_year_to
          } else {
            return true
          }
        })
      } else if (this.memberTypeSearch) {
        return members.filter(item => {
          return item.type.name === this.memberTypeSearch
        }).filter(item => {
          if (this.item.birth_year_from && this.item.birth_year_to) {
            return item.year_of_birth >= this.item.birth_year_from && item.year_of_birth <= this.item.birth_year_to
          } else if (this.item.birth_year_from) {
            return item.year_of_birth >= this.item.birth_year_from
          } else if (this.item.birth_year_from) {
            return item.year_of_birth <= this.item.birth_year_to
          } else {
            return true
          }
        })
      }

      return members.filter(item => {
        if (this.item.birth_year_from && this.item.birth_year_to) {
          return item.year_of_birth >= this.item.birth_year_from && item.year_of_birth <= this.item.birth_year_to
        } else if (this.item.birth_year_from) {
          return item.year_of_birth >= this.item.birth_year_from
        } else if (this.item.birth_year_from) {
          return item.year_of_birth <= this.item.birth_year_to
        } else {
          return true
        }
      })
    },
    selectAllSupplierTypes: {
      get: function () {
        return this.supplierTypes ? this.selectedSupplierTypes.length === this.supplierTypes.length : false
      },
      set: function (value) {
        let selected = []

        if (value) {
          this.supplierTypes.forEach(function (type) {
            selected.push(type.id)
          })
        }

        this.selectedSupplierTypes = selected
      }
    },
    selectAllSuppliers: {
      get: function () {
        return this.suppliers ? this.selectedSuppliers.length === this.suppliers.length : false
      },
      set: function (value) {
        let selected = []

        if (value) {
          this.suppliers.forEach(function (type) {
            selected.push(type.id)
          })
        }

        this.selectedSuppliers = selected
      }
    },
    selectAllMembers: {
      get: function () {
        return this.members ? this.selectedMembers.length === this.members.length : false
      },
      set: function (value) {
        let selected = []

        if (value) {
          this.members.forEach(function (type) {
            selected.push(type.id)
          })
        }

        this.selectedMembers = selected
      }
    }
  }
}
</script>

<style scoped>

</style>
